import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, Card, EmptyState } from "@/components/Shell";
import StatusBadge from "@/components/StatusBadge";
import { Plus, Truck } from "lucide-react";

const DISPATCH_STATUSES = ["Created","Packed","Dispatched","In Transit","Delivered","Partially Delivered","Received","Cancelled","Lost / Damaged"];

export default function Dispatches() {
  const [items, setItems] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    movement_type:"Warehouse to Engineer", from_location:"", to_location:"",
    items:[{product_id:"", quantity:1, serial_numbers:""}],
    courier_name:"", tracking_number:"", vehicle_number:"", eway_bill_number:"",
    dispatch_date:"", expected_delivery:"", remark:"",
  });
  const load = () => api.get("/dispatches").then(r => setItems(r.data)).catch(e=>setErr(formatApiError(e)));
  useEffect(() => { load(); api.get("/products").then(r=>setProducts(r.data)); api.get("/warehouses").then(r=>setWarehouses(r.data)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        items: form.items.map(it => ({ product_id: it.product_id, quantity: parseInt(it.quantity)||0,
          serial_numbers: (it.serial_numbers||"").split(",").map(s=>s.trim()).filter(Boolean) })),
      };
      await api.post("/dispatches", payload);
      setShowCreate(false);
      setForm({movement_type:"Warehouse to Engineer", from_location:"", to_location:"",
        items:[{product_id:"", quantity:1, serial_numbers:""}],
        courier_name:"", tracking_number:"", vehicle_number:"", eway_bill_number:"",
        dispatch_date:"", expected_delivery:"", remark:""});
      load();
    } catch (e2) { setErr(formatApiError(e2)); }
  };

  const updateStatus = async (id, status) => {
    try { await api.post(`/dispatches/${id}/status`, { status }); load(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <div className="p-4 md:p-8" data-testid="dispatches-page">
      <PageHeader title="Dispatches & E-way Bills" subtitle="Courier, transport, tracking & POD."
        action={<button onClick={()=>setShowCreate(s=>!s)} data-testid="new-dispatch-btn" className="inline-flex items-center gap-2 bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold text-sm py-2 px-4 rounded-md"><Plus size={14}/> New Dispatch</button>}
      />

      {showCreate && (
        <Card className="p-5 mb-6" data-testid="dispatch-form">
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <select value={form.movement_type} onChange={e=>setForm(f=>({...f, movement_type:e.target.value}))} className="inp" data-testid="disp-type">
                {["Warehouse to Engineer","Warehouse to Warehouse","Engineer to Warehouse","Warehouse to Customer Site","Customer Site to Warehouse","Warehouse to Vendor","Vendor to Warehouse","Internal Transfer"].map(x=><option key={x}>{x}</option>)}
              </select>
              <input required placeholder="From Location" value={form.from_location} onChange={e=>setForm(f=>({...f, from_location:e.target.value}))} className="inp" />
              <input required placeholder="To Location" value={form.to_location} onChange={e=>setForm(f=>({...f, to_location:e.target.value}))} className="inp" />
              <input placeholder="Courier / Transport Name" value={form.courier_name} onChange={e=>setForm(f=>({...f, courier_name:e.target.value}))} className="inp" />
              <input placeholder="Tracking #" value={form.tracking_number} onChange={e=>setForm(f=>({...f, tracking_number:e.target.value}))} className="inp" />
              <input placeholder="Vehicle #" value={form.vehicle_number} onChange={e=>setForm(f=>({...f, vehicle_number:e.target.value}))} className="inp" />
              <input placeholder="E-way Bill #" value={form.eway_bill_number} onChange={e=>setForm(f=>({...f, eway_bill_number:e.target.value}))} className="inp" />
              <input type="date" value={form.dispatch_date} onChange={e=>setForm(f=>({...f, dispatch_date:e.target.value}))} className="inp" />
              <input type="date" placeholder="Expected Delivery" value={form.expected_delivery} onChange={e=>setForm(f=>({...f, expected_delivery:e.target.value}))} className="inp" />
            </div>
            {form.items.map((it,i)=>(
              <div key={i} className="grid grid-cols-12 gap-2">
                <select value={it.product_id} onChange={e=>{const a=[...form.items];a[i]={...a[i],product_id:e.target.value};setForm(f=>({...f,items:a}));}} className="inp col-span-12 md:col-span-5">
                  <option value="">Product</option>
                  {products.map(p => <option key={p.id} value={p.id}>{p.sku} — {p.name}</option>)}
                </select>
                <input type="number" value={it.quantity} onChange={e=>{const a=[...form.items];a[i]={...a[i],quantity:e.target.value};setForm(f=>({...f,items:a}));}} className="inp col-span-3 md:col-span-2" />
                <input placeholder="Serial #s" value={it.serial_numbers} onChange={e=>{const a=[...form.items];a[i]={...a[i],serial_numbers:e.target.value};setForm(f=>({...f,items:a}));}} className="inp col-span-9 md:col-span-5" />
              </div>
            ))}
            <button type="button" onClick={()=>setForm(f=>({...f, items:[...f.items,{product_id:"",quantity:1,serial_numbers:""}]}))} className="text-xs font-semibold text-[#F97316]">+ Add item</button>
            {err && <div className="text-rose-600 text-sm">{err}</div>}
            <button type="submit" data-testid="disp-save-btn" className="bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold text-sm py-2 px-4 rounded-md">Create Dispatch</button>
          </form>
        </Card>
      )}

      {err && <div className="text-rose-600 text-sm mb-3">{err}</div>}
      {items.length === 0 ? <EmptyState title="No dispatches" icon={Truck} /> :
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm data-table">
              <thead className="bg-zinc-50">
                <tr>
                  <th className="px-4 py-3 text-left">Dispatch #</th>
                  <th className="px-4 py-3 text-left">Type</th>
                  <th className="px-4 py-3 text-left">From → To</th>
                  <th className="px-4 py-3 text-left">Courier</th>
                  <th className="px-4 py-3 text-left">E-way #</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Update</th>
                </tr>
              </thead>
              <tbody>
                {items.map(d => (
                  <tr key={d.id} className="border-t border-zinc-100 hover:bg-zinc-50">
                    <td className="px-4 py-3 num font-semibold">{d.dispatch_no}</td>
                    <td className="px-4 py-3 text-xs">{d.movement_type}</td>
                    <td className="px-4 py-3 text-xs">{d.from_location} → {d.to_location}</td>
                    <td className="px-4 py-3">{d.courier_name || "—"}<div className="text-[11px] text-zinc-500 num">{d.tracking_number}</div></td>
                    <td className="px-4 py-3 num text-xs">{d.eway_bill_number || "—"}</td>
                    <td className="px-4 py-3"><StatusBadge status={d.status} /></td>
                    <td className="px-4 py-3">
                      <select onChange={e=>e.target.value && updateStatus(d.id, e.target.value)} className="inp text-xs" value="" data-testid={`disp-status-${d.dispatch_no}`}>
                        <option value="">Change…</option>
                        {DISPATCH_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      }
      <style>{`.inp { width:100%; border:1px solid #E4E4E7; background:#fff; border-radius:.375rem; padding:.5rem .75rem; font-size:.85rem; outline:none } .inp:focus { box-shadow: 0 0 0 2px #F97316; border-color: transparent }`}</style>
    </div>
  );
}
